import { schema, t } from "@colyseus/schema";
// One input per fixed step. Flat primitives only. Edges (a press) are derived from consecutive
// held values on both sides, so a lost or duplicated frame can never half apply an action.
export const MoveInput = schema({
    moveX: t.int8().default(0),
    moveZ: t.int8().default(0),
    jump: t.boolean().default(false), // sent true on exactly one step
    crouch: t.boolean().default(false),
    fire: t.boolean().default(false), // held: pin pulled. Release throws.
    kick: t.boolean().default(false), // held, edge kicks
    stab: t.boolean().default(false), // held, edge stabs
    interact: t.boolean().default(false), // held, edge swaps at a pad
    slot: t.uint8().default(1), // wanted weapon slot 1 to 4
    yaw: t.float32().default(0),
    pitch: t.float32().default(0),
    // The server step this client was drawing other players at when it sent this. The server uses it
    // to rewind a stab target by the network delay, and nothing else.
    viewStep: t.uint32().default(0),
    // A client generated id, one per input, never reused. When an input produces a throw the server
    // stamps the grenade with it, so the client can match that grenade to the exact throw it
    // predicted instead of guessing from arrival order.
    seq: t.uint32().default(0),
}, "MoveInput");
export const Player = schema({
    name: t.string().default(""),
    colour: t.uint8().default(0),
    bot: t.boolean().default(false),
    connected: t.boolean().default(true),
    // movement, predicted
    x: t.float32().default(0),
    y: t.float32().default(0),
    z: t.float32().default(0),
    vx: t.float32().default(0),
    vy: t.float32().default(0),
    vz: t.float32().default(0),
    px: t.float32().default(0), // knockback shove, decays
    pz: t.float32().default(0),
    yaw: t.float32().default(0),
    pitch: t.float32().default(0),
    grounded: t.boolean().default(false),
    crouch: t.boolean().default(false),
    // weapons, predicted. Relative counters in steps so client and server agree without a shared clock.
    slot: t.uint8().default(1), // 1 Frag, 2 and 3 specials, 4 knife
    slotReq: t.uint8().default(1), // last requested slot, so a change is an edge and a pickup swap sticks
    kind2: t.uint8().default(0), // Kind in slot 2, 0 empty
    ammo2: t.uint8().default(0),
    kind3: t.uint8().default(0),
    ammo3: t.uint8().default(0),
    cookLeft: t.int16().default(-1), // steps of fuse left while holding, -1 not cooking
    cookKind: t.uint8().default(0),
    fragCooldown: t.uint8().default(0), // steps until the next Frag
    stabLeft: t.uint8().default(0), // steps of windup plus recovery left
    kickCooldown: t.uint8().default(0),
    knifeOut: t.boolean().default(false), // thrown and not back yet
    knifeReturn: t.uint16().default(0), // steps until it comes back on its own
    fireHeld: t.boolean().default(false), // last seen fire, for the edge
    kickHeld: t.boolean().default(false),
    stabHeld: t.boolean().default(false),
    interactHeld: t.boolean().default(false),
    // life, server only
    shield: t.float32().default(100),
    flashing: t.boolean().default(false),
    regenWait: t.uint16().default(0),
    alive: t.boolean().default(true),
    respawnLeft: t.uint16().default(0),
    score: t.uint16().default(0),
    kills: t.uint16().default(0),
    deaths: t.uint16().default(0),
    vote: t.boolean().default(false),
}, "Player");
export var GrenadeState;
(function (GrenadeState) {
    GrenadeState[GrenadeState["Flying"] = 0] = "Flying";
    GrenadeState[GrenadeState["Resting"] = 1] = "Resting";
    GrenadeState[GrenadeState["Stuck"] = 2] = "Stuck";
    GrenadeState[GrenadeState["Exploded"] = 3] = "Exploded";
})(GrenadeState || (GrenadeState = {}));
export const Grenade = schema({
    owner: t.string().default(""),
    kind: t.uint8().default(1),
    x: t.float32().default(0),
    y: t.float32().default(0),
    z: t.float32().default(0),
    vx: t.float32().default(0),
    vy: t.float32().default(0),
    vz: t.float32().default(0),
    fuseLeft: t.int16().default(0), // steps until it goes off, -1 for contact only
    state: t.uint8().default(0),
    stuckTo: t.string().default(""), // session id when stuck to a player
    throwSeq: t.uint32().default(0), // the owner's input seq that threw it, for spawn correlation
    bornStep: t.uint32().default(0),
}, "Grenade");
export const Knife = schema({
    owner: t.string().default(""),
    x: t.float32().default(0),
    y: t.float32().default(0),
    z: t.float32().default(0),
    vx: t.float32().default(0),
    vy: t.float32().default(0),
    vz: t.float32().default(0),
    stuck: t.boolean().default(false),
}, "Knife");
export const Pad = schema({
    kind: t.uint8().default(0),
    x: t.float32().default(0),
    y: t.float32().default(0),
    z: t.float32().default(0),
    active: t.boolean().default(false),
    clock: t.boolean().default(false),
    nextIn: t.uint16().default(0), // steps until the item is back, 0 when active
    label: t.string().default(""),
}, "Pad");
export const Drop = schema({
    kind: t.uint8().default(0),
    count: t.uint8().default(0),
    x: t.float32().default(0),
    y: t.float32().default(0),
    z: t.float32().default(0),
    left: t.uint16().default(0),
}, "Drop");
export var Phase;
(function (Phase) {
    Phase[Phase["Lobby"] = 0] = "Lobby";
    Phase[Phase["Countdown"] = 1] = "Countdown";
    Phase[Phase["Playing"] = 2] = "Playing";
    Phase[Phase["Overtime"] = 3] = "Overtime";
    Phase[Phase["Results"] = 4] = "Results";
})(Phase || (Phase = {}));
export const ArenaState = schema({
    players: t.map(Player),
    grenades: t.map(Grenade),
    knives: t.map(Knife),
    pads: t.map(Pad),
    drops: t.map(Drop),
    tick: t.uint32().default(0),
    phase: t.uint8().default(0),
    phaseLeft: t.uint32().default(0), // steps left in the current phase
    matchStep: t.uint32().default(0), // steps since the match started, drives the pad clock
    winner: t.string().default(""),
    humans: t.uint8().default(0),
}, "ArenaState");
/** The fields the client predicts and the server reconciles. Everything the step reads or writes. */
export const PREDICTED_FIELDS = [
    "x", "y", "z", "vx", "vy", "vz", "px", "pz", "grounded", "crouch",
    "slot", "slotReq", "kind2", "ammo2", "kind3", "ammo3", "cookLeft", "cookKind", "fragCooldown",
    "stabLeft", "kickCooldown", "knifeOut", "knifeReturn", "fireHeld", "kickHeld", "stabHeld", "interactHeld",
];
//# sourceMappingURL=schema.js.map